import { type ReactElement } from 'react'
import { observer } from 'mobx-react-lite'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { Button } from '@/components/Button'
import type ConsultationManagerViewModel from '@/features/jobs/details/consultation/ConsultationManagerViewModel'
import { ScrolledAction } from '@/features/jobs/details/components/PageHeader'
import { ButtonContext, Provider } from 'react-aria-components'

interface TranscriptDownloadOption {
  /**
   * Label for the download option.
   */
  label: string
  /**
   * On press handler for the download option.
   */
  onPress?: () => void
}

interface TranscriptDownloadDropdownProps {
  /**
   * Download options for the dropdown.
   */
  options: TranscriptDownloadOption[]
  /**
   * The variant to use for the button.
   */
  variant?: 'button' | 'scrolled-action'
  /**
   * Test ID attribute for the dropdown button.
   */
  buttonTestId?: string
  /**
   * Test ID attribute for the dropdown menu items.
   */
  transcriptMenuItemTestId?: string
}

/**
 * A dropdown that adapts between a single button and dropdown based on number of transcripts.
 */
export function TranscriptDownloadDropdown({
  options,
  variant = 'button',
  buttonTestId,
  transcriptMenuItemTestId,
}: TranscriptDownloadDropdownProps): ReactElement {
  const element =
    variant === 'button' ? (
      <Button label="Download" variant="text-inline" testId={buttonTestId} />
    ) : (
      <ScrolledAction label="Download" />
    )

  return options.length === 1 ? (
    <Provider values={[[ButtonContext, { onPress: options[0].onPress }]]}>
      {element}
    </Provider>
  ) : (
    <Dropdown
      title="Available transcripts"
      placement="bottom"
      offset={12}
      trigger={element}
    >
      {options.map((option, index) => (
        <DropdownItem
          key={option.label}
          onAction={option.onPress}
          testId={
            transcriptMenuItemTestId
              ? `${transcriptMenuItemTestId}-item-${index}`
              : undefined
          }
        >
          {option.label}
        </DropdownItem>
      ))}
    </Dropdown>
  )
}

interface TranscriptDownloadDropdownContainerProps {
  vm: ConsultationManagerViewModel
  variant?: 'button' | 'scrolled-action'
}

/**
 * Transcript download dropdown container.
 */
export const TranscriptDownloadDropdownContainer = observer(
  function TranscriptDownloadDropdownContainer({
    vm,
    variant = 'button',
  }: TranscriptDownloadDropdownContainerProps) {
    return (
      <TranscriptDownloadDropdown
        variant={variant}
        options={vm.transcriptVms.map((vm) => ({
          label: vm.doc.title,
          onPress: () => vm.download(),
        }))}
      />
    )
  },
)
